<template>
  <div class="product-select-input" ref="container">
    <!-- Preview Mode (when not focused) -->
    <div v-if="!isOpen" class="select-preview" :class="{ disabled: disabled }" @click="!disabled && openDropdown()"
      :tabindex="disabled ? -1 : 0" @keydown.enter="!disabled && openDropdown()"
      @keydown.space.prevent="!disabled && openDropdown()">
      <div v-if="displayProduct" class="preview-content">
        <span class="preview-text">{{ displayProduct.displayName }}</span>
      </div>
      <span v-else class="placeholder-text">Select product</span>
    </div>

    <!-- Input Mode (when focused) -->
    <div v-else class="select-active">
      <input ref="searchInput" v-model="searchTerm" type="text" class="search-input" placeholder="Search products..."
        @input="handleSearch" @keydown.esc="closeDropdown" @blur="handleBlur" />
    </div>

    <!-- Dropdown Menu (teleported to body) -->
    <Teleport to="body">
      <div v-if="isOpen && dropdownPosition" class="dropdown-menu" :style="{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`
      }">
        <div v-if="loading" class="dropdown-item loading-item">
          Loading...
        </div>
        <div v-else-if="filteredProducts.length === 0" class="dropdown-item empty-item">
          No products found
        </div>
        <button v-else v-for="product in filteredProducts" :key="product.productId" class="dropdown-item"
          :class="{ 'deleted-item': product.isDeleted }"
          @mousedown.prevent="selectProduct(product)">
          <span class="item-text">{{ getDisplayName(product) }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script>
import * as api from '@/api'
import { queryClient } from '@/libs/queryClient'

export default {
  name: 'ProductSelectInput',
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: null
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowDeleted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchTerm: '',
      products: [],
      displayProduct: null,
      loading: false,
      dropdownPosition: null
    }
  },
  computed: {
    filteredProducts() {
      if (!this.searchTerm) {
        return this.products
      }
      const search = this.searchTerm.toLowerCase()
      return this.products.filter(product =>
        product.name?.toLowerCase().includes(search)
      )
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.displayProduct) {
          this.fetchProductForPreview(newVal)
        }
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.updateDropdownPosition, true)
    window.addEventListener('resize', this.updateDropdownPosition)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
    window.removeEventListener('resize', this.updateDropdownPosition)
  },
  methods: {
    async openDropdown() {
      this.isOpen = true
      this.searchTerm = ''
      this.updateDropdownPosition()
      await this.fetchProducts()
      this.$nextTick(() => {
        this.$refs.searchInput?.focus()
      })
    },
    closeDropdown() {
      this.isOpen = false
      this.searchTerm = ''
      this.dropdownPosition = null
    },
    updateDropdownPosition() {
      if (!this.isOpen || !this.$refs.container) return

      const rect = this.$refs.container.getBoundingClientRect()
      const dropdownMaxHeight = 300
      const gap = 4
      const viewportHeight = window.innerHeight

      // Check if dropdown would overflow below
      const spaceBelow = viewportHeight - rect.bottom - gap
      const spaceAbove = rect.top - gap

      let top
      if (spaceBelow >= dropdownMaxHeight || spaceBelow >= spaceAbove) {
        // Position below
        top = rect.bottom + gap
      } else {
        // Position above
        top = rect.top - gap - Math.min(dropdownMaxHeight, spaceAbove)
      }

      this.dropdownPosition = {
        top,
        left: rect.left,
        width: rect.width
      }
    },
    handleBlur() {
      // Delay to allow click events to fire
      setTimeout(() => {
        this.closeDropdown()
      }, 200)
    },
    handleSearch() {
      // Client-side filtering via computed property
      // No debouncing needed since it's instant
    },
    async fetchProducts() {
      this.loading = true
      try {
        const params = this.allowDeleted ? { includeDeleted: true } : {}
        const response = await queryClient.fetchQuery({
          queryKey: ['products', { includeDeleted: this.allowDeleted }],
          queryFn: () => api.fetchProducts(params)
        })
        this.products = response.products || []
      } catch (error) {
        console.error('Error fetching products:', error)
        this.products = []
      } finally {
        this.loading = false
      }
    },
    async fetchProductForPreview(productId) {
      if (!productId) {
        this.displayProduct = null
        return
      }

      try {
        const product = await queryClient.fetchQuery({
          queryKey: ['product', productId],
          queryFn: () => api.fetchProductById(productId)
        })
        this.displayProduct = {
          ...product,
          displayName: this.getDisplayName(product)
        }
      } catch (error) {
        console.error('Error fetching product for preview:', error)
        this.displayProduct = null
      }
    },
    selectProduct(product) {
      this.$emit('update:modelValue', product.productId)
      this.displayProduct = {
        ...product,
        displayName: this.getDisplayName(product)
      }
      this.closeDropdown()
    },
    getDisplayName(product) {
      const name = product.name?.trim() || 'Unknown'
      return product.isDeleted ? `${name} (deleted)` : name
    }
  }
}
</script>

<style scoped>
.product-select-input {
  width: 100%;
}

/* Preview Mode Styles */
.select-preview {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: pointer;
  height: 31px;
  display: flex;
  align-items: center;
}

.select-preview:hover {
  border-color: #9ca3af;
}

.select-preview:focus {
  outline: none;
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
}

.select-preview.disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
}

.select-preview.disabled .preview-text,
.select-preview.disabled .placeholder-text {
  color: #9ca3af;
}

.preview-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.preview-text {
  font-size: 14px;
  color: #333;
  line-height: 1;
}

.placeholder-text {
  color: #9ca3af;
  font-size: 14px;
}

/* Active Mode Styles */
.search-input {
  width: 100%;
  padding: 4px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  background-color: #fff;
  border: 1px solid #4338ca;
  border-radius: var(--border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.1);
  height: 31px;
}

.search-input:focus {
  outline: none;
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Dropdown Menu (Teleported) */
.dropdown-menu {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: var(--border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 14px;
  color: #333;
}

.dropdown-item:hover {
  background-color: #f3f4f6;
}

.dropdown-item:first-child {
  border-radius: 0 8px 0 0;
}

.dropdown-item:last-child {
  border-radius: 0 0 8px 8px;
}

.deleted-item {
  color: #9ca3af;
  font-style: italic;
}

.loading-item,
.empty-item {
  color: #9ca3af;
  font-style: italic;
  cursor: default;
}

.loading-item:hover,
.empty-item:hover {
  background-color: transparent;
}
</style>
